import ResponsiveImage from "@/app/(site)/_components/GCMolecules/ResponsiveImage"
import HeadingBlock from "@/app/(site)/_components/GCSanityBlocks/HeadingBlock"
import MediaBlock from "@/app/(site)/_components/GCSanityBlocks/MediaBlock"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { clsx } from "@/src/utils/clsx"
import { getGCGeneralLinkHref } from "@/src/utils/links/getGCGeneralLinkHref"
import {
    type HeadingBlockType,
    type HeadingLevel,
    type HybridMediaBlockData
} from "@/types/gc-modules-types"

type HybridMediaBlockProps = {
    className?: string
    data: HybridMediaBlockData
    isAboveTheFold?: boolean
    headingLevelOverride?: HeadingLevel
}

export default function HybridMediaBlock({
    className,
    data,
    isAboveTheFold,
    headingLevelOverride
}: HybridMediaBlockProps) {
    if (!data) {
        return null
    }

    const {
        contentPlacement,
        headingBlock: headingBlocks,
        mediaBlock,
        textColor,
        imageSizes,
        link
    } = data

    const headingBlock = headingBlocks?.[0] ?? null

    const getContentStyling = () => {
        switch (contentPlacement) {
            case "bottom-left":
                return "items-end pb-32 sm:pb-40 md:pb-48"
            case "bottom-right":
                return "items-end justify-end pb-32 sm:pb-40 md:pb-48"
            case "bottom":
                return "items-end pb-32 sm:pb-40 md:pb-48"
            default:
                return "bottom-0 left-0 p-4"
        }
    }

    const hasSeparateDesktop = !!mediaBlock?.desktop
    const desktopMedia = mediaBlock?.desktop ?? mediaBlock?.mobile

    const getGradientDesktop = () => {
        if (desktopMedia.colorGroup || !headingBlock) return ""
        switch (contentPlacement) {
            case "bottom-left":
                return "linear-gradient(250deg, rgba(0, 0, 0, 0.00) 35%, rgba(0, 0, 0, 0.80) 100%)"
            case "bottom-right":
                return "linear-gradient(106deg, rgba(0, 0, 0, 0.00) 35%, rgba(0, 0, 0, 0.80) 100%)"
            case "bottom":
                return "linear-gradient(184deg, rgba(0, 0, 0, 0.00) 34.3%, rgba(0, 0, 0, 0.80) 97.04%)"
            default:
                return ""
        }
    }

    const getGradientMobile = () => {
        if (mediaBlock.mobile.colorGroup || !headingBlock) return ""
        return "linear-gradient(180deg, rgba(0, 0, 0, 0.10) 25%, rgba(0, 0, 0, 0.70) 100%)"
    }

    const mediaCaption =
        desktopMedia.imageGroup?.images[0].image.caption ||
        desktopMedia.videoGroup?.videos[0].caption

    // Control height of gradient offset based on if there is a caption or not.
    // This is to avoid the gradient covering the media caption.
    const gradientHeight = mediaCaption ? "calc(100% - 1.5rem)" : "100%"

    const updatedHeadingBlock: HeadingBlockType | null =
        contentPlacement === "bottom"
            ? { ...headingBlock, direction: "horizontal" }
            : headingBlock

    const generalLink = link ? getGCGeneralLinkHref(link) : undefined

    const desktopImageEl = mediaBlock?.desktop?.imageGroup?.images?.[0]
    const mobileImageEl = mediaBlock?.mobile?.imageGroup?.images?.[0]

    // When both breakpoints are (Sanity) images we can collapse to a single
    // native <picture> element so only the matching asset is downloaded. Video,
    // colour and mixed media can't live in a <picture>, so they keep the
    // CSS-toggled two-element fallback.
    const isSanityImageRef = (ref?: string) => !!ref && ref.startsWith("image-")
    const canUseResponsivePicture =
        hasSeparateDesktop &&
        !!desktopImageEl &&
        !!mobileImageEl &&
        isSanityImageRef(desktopImageEl.image.asset?._ref) &&
        isSanityImageRef(mobileImageEl.image.asset?._ref)

    const mediaLayer = canUseResponsivePicture ? (
        <ResponsiveImage
            desktopImage={desktopImageEl!}
            mobileImage={mobileImageEl!}
            isAboveTheFold={isAboveTheFold}
            sizes={imageSizes}
        />
    ) : hasSeparateDesktop ? (
        <>
            <div className="hidden h-full w-full md:block">
                <MediaBlock
                    imageSizes={imageSizes}
                    isAboveTheFold={isAboveTheFold}
                    media={desktopMedia}
                />
            </div>
            <div className="block h-full w-full md:hidden">
                <MediaBlock
                    imageSizes={imageSizes}
                    isAboveTheFold={isAboveTheFold}
                    media={mediaBlock.mobile}
                />
            </div>
        </>
    ) : (
        <MediaBlock
            imageSizes={imageSizes}
            isAboveTheFold={isAboveTheFold}
            media={mediaBlock.mobile}
        />
    )

    const headingOverlay = updatedHeadingBlock && (
        <>
            <div
                aria-hidden
                className="pointer-events-none absolute top-0 hidden w-full md:block"
                style={{ background: getGradientDesktop(), height: gradientHeight }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute top-0 block w-full md:hidden"
                style={{ background: getGradientMobile(), height: gradientHeight }}
            />
            <div
                className={clsx(
                    "pointer-events-none absolute top-0 flex w-full",
                    "p-12 sm:p-16 md:p-24",
                    getContentStyling()
                )}
                style={{ height: gradientHeight }}
            >
                <HeadingBlock
                    className={clsx({ "pointer-events-auto": !generalLink })}
                    data={updatedHeadingBlock}
                    isAboveTheFold={isAboveTheFold}
                    textColor={textColor || "text-main-white"}
                    headingLevelOverride={headingLevelOverride}
                />
            </div>
        </>
    )

    const wrapperClassName = clsx("relative w-full", className)

    return (
        <div className={wrapperClassName}>
            {generalLink ? (
                <DynamicLink href={generalLink} className="block h-full w-full">
                    {mediaLayer}
                </DynamicLink>
            ) : (
                mediaLayer
            )}
            {headingOverlay}
        </div>
    )
}
